import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';

const PRIVACY_POLICY_META = {
  lastUpdated: 'March 2, 2025',
  privacyContactEmail: 'contact@penchukcyber.com',
  companyLegalName: 'Penchuk Cyber Ltd.',
  companyAddress: 'Azrieli Sarona Tower, 121-123 Derech Menachem Begin, Tel-Aviv, Israel',
  websiteUrl: 'www.penchukcyber.com',
};

const Privacy: React.FC = () => {
  const sectionTitleProps = { sx: { mt: 3, mb: 1.5 } };
  const subsectionProps = { sx: { mt: 2, mb: 1 } };
  const bodyProps = { variant: 'body1' as const, sx: { mb: 1.5, maxWidth: '72ch', lineHeight: 1.6 } };
  const listProps = { component: 'ul' as const, sx: { pl: 2.5, mb: 1.5 } };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      {/* Top bar: title + Home button */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="h1">
              Privacy Policy
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={{ textDecoration: 'none' }}
            >
              Home
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3, px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last Updated: {PRIVACY_POLICY_META.lastUpdated}
        </Typography>

        {/* 1. Introduction */}
        <Typography variant="h5" {...sectionTitleProps}>
          1. Introduction
        </Typography>
        <Typography {...bodyProps}>
          Welcome to CaosBlitz (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz is a cybersecurity platform specializing in DDoS attack simulations, testing, and resilience assessment. We are committed to protecting your privacy and handling your information transparently and securely.
        </Typography>
        <Typography {...bodyProps}>
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, platform, simulations, services, and related tools (collectively, the &quot;Services&quot;).
        </Typography>
        <Typography {...bodyProps}>
          By using CaosBlitz, you agree to the practices described in this Privacy Policy.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 2. Information We Collect */}
        <Typography variant="h5" {...sectionTitleProps}>
          2. Information We Collect
        </Typography>
        <Typography {...bodyProps}>
          We collect information necessary to provide secure and effective DDoS simulation and testing services.
        </Typography>

        <Typography variant="h6" {...subsectionProps}>
          2.1 Information You Provide
        </Typography>
        <Typography {...bodyProps}>
          You may provide us with:
        </Typography>
        <Typography {...listProps}>
          <li>Name and contact details (email, phone, company)</li>
          <li>Account credentials</li>
          <li>Organization information</li>
          <li>Billing and payment details</li>
          <li>Support requests and communications</li>
          <li>Simulation configuration inputs</li>
          <li>Documentation uploaded to the platform</li>
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" {...subsectionProps}>
          2.2 Technical and Usage Information
        </Typography>
        <Typography {...bodyProps}>
          When you use CaosBlitz, we may automatically collect:
        </Typography>
        <Typography {...listProps}>
          <li>IP addresses</li>
          <li>Device and browser information</li>
          <li>Log data and timestamps</li>
          <li>API calls and usage metrics</li>
          <li>Simulation parameters and execution logs</li>
          <li>Performance metrics and traffic metadata</li>
        </Typography>
        <Typography {...bodyProps}>
          We do not collect the content of your production systems beyond what is required to perform the simulation or analysis you explicitly initiate.
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" {...subsectionProps}>
          2.3 Simulation Data
        </Typography>
        <Typography {...bodyProps}>
          During DDoS simulations, we may process:
        </Typography>
        <Typography {...listProps}>
          <li>Target infrastructure identifiers (IP ranges, domains, endpoints)</li>
          <li>Network performance indicators</li>
          <li>Traffic behavior data</li>
          <li>Mitigation responses and logs</li>
        </Typography>
        <Typography {...bodyProps}>
          This data is used strictly for testing, analytics, and resilience reporting.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 3. How We Use Information */}
        <Typography variant="h5" {...sectionTitleProps}>
          3. How We Use Information
        </Typography>
        <Typography {...bodyProps}>
          We use collected information to:
        </Typography>
        <Typography {...listProps}>
          <li>Provide and operate the CaosBlitz platform</li>
          <li>Execute DDoS simulations and generate reports</li>
          <li>Improve system reliability and accuracy</li>
          <li>Provide customer support</li>
          <li>Monitor platform security</li>
          <li>Prevent misuse or unauthorized simulations</li>
          <li>Comply with legal obligations</li>
        </Typography>
        <Typography {...bodyProps}>
          We do not sell personal or organizational data.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 4. Legal Basis (GDPR) */}
        <Typography variant="h5" {...sectionTitleProps}>
          4. Legal Basis for Processing (GDPR Compliance)
        </Typography>
        <Typography {...bodyProps}>
          If you are located in the European Economic Area (EEA), we process data based on:
        </Typography>
        <Typography {...listProps}>
          <li>Contractual necessity (to provide Services)</li>
          <li>Legitimate interests (security monitoring and platform improvement)</li>
          <li>Legal obligations</li>
          <li>Your consent, where applicable</li>
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 5. Data Sharing */}
        <Typography variant="h5" {...sectionTitleProps}>
          5. Data Sharing and Disclosure
        </Typography>
        <Typography {...bodyProps}>
          We may share information:
        </Typography>
        <Typography {...listProps}>
          <li>With trusted infrastructure providers (e.g., cloud hosting providers)</li>
          <li>With payment processors for billing</li>
          <li>With legal authorities if required by law</li>
          <li>To prevent fraud, abuse, or unauthorized use</li>
        </Typography>
        <Typography {...bodyProps}>
          All third-party providers are required to maintain appropriate security safeguards.
        </Typography>
        <Typography {...bodyProps}>
          We do not sell or rent your data.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 6. Data Retention */}
        <Typography variant="h5" {...sectionTitleProps}>
          6. Data Retention
        </Typography>
        <Typography {...bodyProps}>
          We retain information only as long as necessary to:
        </Typography>
        <Typography {...listProps}>
          <li>Provide Services</li>
          <li>Maintain audit and security logs</li>
          <li>Comply with legal and regulatory obligations</li>
          <li>Resolve disputes</li>
        </Typography>
        <Typography {...bodyProps}>
          Simulation logs and reports may be retained for security and compliance purposes unless deletion is requested and legally permissible.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 7. Security */}
        <Typography variant="h5" {...sectionTitleProps}>
          7. Security Measures
        </Typography>
        <Typography {...bodyProps}>
          Given the nature of our Services, security is central to our operations.
        </Typography>
        <Typography {...bodyProps}>
          We implement:
        </Typography>
        <Typography {...listProps}>
          <li>Encryption in transit (TLS)</li>
          <li>Access controls and role-based permissions</li>
          <li>Secure cloud infrastructure</li>
          <li>Logging and monitoring systems</li>
          <li>Periodic security reviews</li>
        </Typography>
        <Typography {...bodyProps}>
          However, no system is completely secure, and we cannot guarantee absolute security.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 8. Your Rights */}
        <Typography variant="h5" {...sectionTitleProps}>
          8. Your Rights
        </Typography>
        <Typography {...bodyProps}>
          Depending on your jurisdiction, you may have the right to:
        </Typography>
        <Typography {...listProps}>
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion</li>
          <li>Restrict processing</li>
          <li>Object to processing</li>
          <li>Data portability</li>
          <li>Withdraw consent</li>
        </Typography>
        <Typography {...bodyProps}>
          To exercise these rights, contact us at: {PRIVACY_POLICY_META.privacyContactEmail}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 9. Cookies */}
        <Typography variant="h5" {...sectionTitleProps}>
          9. Cookies and Tracking
        </Typography>
        <Typography {...bodyProps}>
          Our website may use cookies and similar technologies to:
        </Typography>
        <Typography {...listProps}>
          <li>Maintain sessions</li>
          <li>Improve user experience</li>
          <li>Analyze website performance</li>
          <li>Enhance security</li>
        </Typography>
        <Typography {...bodyProps}>
          You may control cookies through your browser settings.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 10. International Transfers */}
        <Typography variant="h5" {...sectionTitleProps}>
          10. International Data Transfers
        </Typography>
        <Typography {...bodyProps}>
          Your information may be processed in countries outside your jurisdiction. Where required, we implement appropriate safeguards such as:
        </Typography>
        <Typography {...listProps}>
          <li>Standard Contractual Clauses (SCCs)</li>
          <li>Contractual data protection agreements</li>
          <li>Secure hosting environments</li>
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 11. Acceptable Use */}
        <Typography variant="h5" {...sectionTitleProps}>
          11. Acceptable Use &amp; Authorized Testing
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz is strictly intended for authorized security testing and simulation purposes only.
        </Typography>
        <Typography {...bodyProps}>
          Users must:
        </Typography>
        <Typography {...listProps}>
          <li>Have explicit authorization to test target infrastructure</li>
          <li>Comply with applicable laws</li>
          <li>Use the platform responsibly</li>
        </Typography>
        <Typography {...bodyProps}>
          We reserve the right to suspend accounts engaged in unauthorized or unlawful testing.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 12. Children */}
        <Typography variant="h5" {...sectionTitleProps}>
          12. Children&apos;s Privacy
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz is not intended for individuals under 18. We do not knowingly collect personal data from minors.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 13. Changes */}
        <Typography variant="h5" {...sectionTitleProps}>
          13. Changes to This Privacy Policy
        </Typography>
        <Typography {...bodyProps}>
          We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised &quot;Last Updated&quot; date.
        </Typography>
        <Typography {...bodyProps}>
          Continued use of the Services after updates constitutes acceptance of the revised policy.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 14. Contact */}
        <Typography variant="h5" {...sectionTitleProps}>
          14. Contact Us
        </Typography>
        <Typography {...bodyProps}>
          If you have questions about this Privacy Policy or data practices, contact:
        </Typography>
        <Box component="address" sx={{ fontStyle: 'normal', ...bodyProps.sx }}>
          <Typography {...bodyProps}>
            CaosBlitz
            <br />
            {PRIVACY_POLICY_META.companyLegalName}
            <br />
            {PRIVACY_POLICY_META.companyAddress}
            <br />
            Email: {PRIVACY_POLICY_META.privacyContactEmail}
            <br />
            Website: {PRIVACY_POLICY_META.websiteUrl}
          </Typography>
        </Box>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{ textDecoration: 'none' }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Privacy;
